import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  buildUnsplashSrcSet,
  HERO_SIZES,
  IMAGE_DIMENSIONS,
  isUnsplashUrl,
  optimizeRemoteImageUrl,
} from '@/lib/imageUrl';
import { removePrerenderShells } from '@/lib/staticHero';
import { HeroCarouselSkeleton } from '@/components/templates/HeroCarouselSkeleton';

export interface CarouselSlide {
  id: string;
  layout?: 'brand' | 'standard';
  /** Full-bleed background image URL. Omit for a solid-colour slide. */
  image?: string;
  /** Tailwind bg class used when there is no image (e.g. 'bg-brand-900') */
  bgClass?: string;
  /** Gradient scrim over the image for readability */
  overlayColor?: string;
  badge?: string;
  headline: string;
  tagline?: string;
  sub: string;
  ctaLabel: string;
  ctaTo: string;
  /** Quick-link pills rendered below the CTA */
  pills?: { label: string; to: string; accent?: boolean }[];
}

interface Props {
  slides: CarouselSlide[];
  intervalMs?: number;
}

function ChevronLeft() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

const BRAND_HERO_MAX_PILLS = 6;

function HeroPillLink({
  pill,
  tabIndex,
}: {
  pill: NonNullable<CarouselSlide['pills']>[number];
  tabIndex?: number;
}) {
  return (
    <Link
      to={pill.to}
      tabIndex={tabIndex}
      className={clsx(
        'inline-flex min-h-9 w-full items-center justify-center rounded-full px-3 py-1.5 text-xs font-medium transition sm:min-h-10 sm:text-sm',
        pill.accent
          ? 'border border-accent-300 bg-accent-500/90 text-brand-900 hover:bg-accent-400'
          : 'border border-white/30 bg-white/15 text-white hover:border-white/45 hover:bg-white/25',
      )}
    >
      {pill.label}
    </Link>
  );
}

function HeroSlideContent({ slide, isActive }: { slide: CarouselSlide; isActive: boolean }) {
  const isBrand = slide.layout === 'brand';
  const pills = slide.pills?.slice(0, BRAND_HERO_MAX_PILLS) ?? [];
  const tabIndex = isActive ? undefined : -1;

  return (
    <div
      className={clsx(
        'flex h-full min-h-0 w-full items-center pb-14 pt-6 sm:pb-16',
        !isActive && 'pointer-events-none',
      )}
      aria-hidden={!isActive}
    >
      <div className="mx-auto w-full max-w-[1400px] px-4 sm:px-8">
        <div className={clsx(isBrand ? 'mx-auto max-w-2xl text-center lg:max-w-3xl' : 'max-w-lg')}>
          {slide.badge ? (
            <span className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/10 px-2.5 py-0.5 text-[11px] font-semibold text-white/95 backdrop-blur-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
              {slide.badge}
            </span>
          ) : null}

          {isBrand ? (
            <>
              <h1 className="text-3xl font-bold leading-[1.1] tracking-tight text-white drop-shadow-sm sm:text-4xl lg:text-[2.75rem]">
                {slide.headline}
              </h1>
              {slide.tagline ? (
                <p className="mt-2 text-base font-semibold text-white/90 sm:text-lg">{slide.tagline}</p>
              ) : null}
              {slide.sub ? (
                <p className="mx-auto mt-2 hidden max-w-lg text-sm leading-relaxed text-white/75 md:block">
                  {slide.sub}
                </p>
              ) : null}
            </>
          ) : (
            <>
              <h2 className="text-xl font-bold leading-tight text-white drop-shadow-sm sm:text-3xl lg:text-4xl">
                {slide.headline}
              </h2>
              {slide.sub ? (
                <p className="mt-1.5 hidden text-sm leading-snug text-white/80 sm:block">{slide.sub}</p>
              ) : null}
            </>
          )}

          <div className={clsx('mt-4 sm:mt-5', isBrand && 'flex justify-center')}>
            <Link
              to={slide.ctaTo}
              tabIndex={tabIndex}
              className="inline-flex items-center gap-2 rounded-lg bg-accent-500 px-5 py-2.5 text-sm font-bold text-brand-900 shadow-lg transition hover:bg-accent-400 active:scale-[0.98] sm:text-base"
            >
              {slide.ctaLabel}
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                aria-hidden
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>

          {pills.length > 0 ? (
            <nav
              className={clsx('mt-5 sm:mt-6', isBrand && 'mx-auto max-w-md sm:max-w-xl')}
              aria-label="Quick links"
            >
              {isBrand ? (
                <>
                  <p className="mb-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/80">
                    Explore
                  </p>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {pills.map((p) => (
                      <HeroPillLink key={`${p.to}-${p.label}`} pill={p} tabIndex={tabIndex} />
                    ))}
                  </div>
                </>
              ) : (
                <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {pills.map((p) => (
                    <HeroPillLink key={`${p.to}-${p.label}`} pill={p} tabIndex={tabIndex} />
                  ))}
                </div>
              )}
            </nav>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function HeroCarousel({ slides = [], intervalMs = 5500 }: Props) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const go = useCallback(
    (index: number) => {
      if (slides.length === 0) return;
      setCurrent((index + slides.length) % slides.length);
    },
    [slides.length],
  );

  const next = useCallback(() => go(current + 1), [current, go]);
  const prev = useCallback(() => go(current - 1), [current, go]);

  useLayoutEffect(() => {
    // Keep static hero until React has real slides (avoids blank band after shell removal).
    if (slides.length === 0) return;
    removePrerenderShells();
  }, [slides.length]);

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    timerRef.current = setInterval(next, intervalMs);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [paused, next, intervalMs, slides.length]);

  useEffect(() => {
    setCurrent((i) => (slides.length === 0 ? 0 : i >= slides.length ? 0 : i));
  }, [slides.length]);

  useEffect(() => {
    if (slides.length === 0) return;
    const next = slides[(current + 1) % slides.length];
    if (!next?.image) return;
    const img = new Image();
    img.src = isUnsplashUrl(next.image)
      ? optimizeRemoteImageUrl(next.image, IMAGE_DIMENSIONS.hero.width)
      : next.image;
    if (isUnsplashUrl(next.image)) {
      img.srcset = buildUnsplashSrcSet(next.image);
      img.sizes = HERO_SIZES;
    }
  }, [current, slides]);

  if (slides.length === 0) {
    return <HeroCarouselSkeleton />;
  }

  return (
    <div
      data-hero-carousel
      className="relative isolate overflow-hidden bg-brand-900"
      style={{ height: 'clamp(320px, 48vw, 520px)' }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {slides.map((s, i) => {
        const isActive = i === current;
        return (
          <div
            key={s.id}
            role="group"
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${slides.length}`}
            aria-hidden={!isActive}
            className={clsx(
              'absolute inset-0 transition-opacity duration-500 ease-out',
              isActive ? 'z-[1] opacity-100' : 'z-0 opacity-0 invisible',
            )}
          >
            {s.image ? (
              <>
                {isActive ? (
                  <img
                    src={
                      isUnsplashUrl(s.image)
                        ? optimizeRemoteImageUrl(s.image, IMAGE_DIMENSIONS.hero.width)
                        : s.image
                    }
                    srcSet={isUnsplashUrl(s.image) ? buildUnsplashSrcSet(s.image) : undefined}
                    sizes={isUnsplashUrl(s.image) ? HERO_SIZES : undefined}
                    alt=""
                    width={IMAGE_DIMENSIONS.hero.width}
                    height={IMAGE_DIMENSIONS.hero.height}
                    decoding="async"
                    className="h-full w-full object-cover object-center"
                    loading="eager"
                    fetchPriority="high"
                  />
                ) : (
                  <div className="absolute inset-0 bg-brand-900" aria-hidden />
                )}
                <div
                  className={`absolute inset-0 bg-gradient-to-r ${s.overlayColor ?? 'from-black/70 via-black/40 to-transparent'}`}
                />
              </>
            ) : (
              <>
                <div className={`absolute inset-0 ${s.bgClass ?? 'bg-brand-900'}`} />
                <div
                  className="pointer-events-none absolute inset-0 opacity-[0.06]"
                  style={{
                    backgroundImage:
                      'radial-gradient(circle at 15% 50%, #FCD116 0%, transparent 55%), radial-gradient(circle at 85% 20%, #CE1126 0%, transparent 40%)',
                  }}
                />
              </>
            )}

            <div className="absolute inset-0 z-[2]">
              <HeroSlideContent slide={s} isActive={isActive} />
            </div>
          </div>
        );
      })}

      {slides.length > 1 ? (
        <>
          <button
            type="button"
            onClick={prev}
            aria-label="Previous slide"
            className="absolute left-2 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/30 text-white backdrop-blur-sm transition hover:bg-black/50 sm:left-4"
          >
            <ChevronLeft />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Next slide"
            className="absolute right-2 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/30 text-white backdrop-blur-sm transition hover:bg-black/50 sm:right-4"
          >
            <ChevronRight />
          </button>

          <div
            className="absolute bottom-3 left-1/2 z-20 flex max-w-[min(100%,16rem)] -translate-x-1/2 items-center justify-center gap-0.5 px-2 sm:max-w-none"
            role="tablist"
            aria-label="Hero slides"
          >
            {slides.map((s, i) => (
              <button
                key={s.id}
                type="button"
                role="tab"
                onClick={() => go(i)}
                aria-label={`Go to slide ${i + 1}: ${s.headline}`}
                aria-selected={i === current}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full sm:h-8 sm:w-8"
              >
                <span
                  className={clsx(
                    'block h-1.5 rounded-full transition-all duration-300',
                    i === current ? 'w-5 bg-accent-500' : 'w-1.5 bg-white/50',
                  )}
                />
              </button>
            ))}
          </div>
        </>
      ) : null}

      {!paused && slides.length > 1 ? (
        <div
          key={current}
          className="absolute bottom-0 left-0 z-20 h-0.5 w-full bg-accent-500/60"
          style={{ animation: `hero-grow ${intervalMs}ms linear`, transformOrigin: 'left' }}
        />
      ) : null}

      <style>{`
        @keyframes hero-grow {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
      `}</style>
    </div>
  );
}
